import { Cartesian2, Cartesian3 } from "cesium";
import { HALinearSegment } from "./horizontal-alignment-linear-segment";
import { CurveDataType, DEFAULT_SPEED_KMPH, HorizontalAlignmentType, } from "./interface";
import { generateCompositeAlignmentFromIPs_CurveOnly } from "./ip-curve-generator";
import { HACircularSegment } from "./horizontal-alignment-circular-segment";
import * as HAUtils from "./horizontal-alignment-utils";
/**
 * Simple Curve Design
 * This design consists of a straight section followed by a single arc of a constant radius.
 * Technical Name: Circular Curve or Simple Circular Curve.
 * Characteristics: The road transitions directly from a straight line (infinite radius) to a curve
 * with a fixed radius $R$. Because this creates an abrupt change in centrifugal force, it is generally
 * reserved for low-speed roads or situations with significant space constraints.
 */
export class SimpleCircularHorizontalAlignment {
    type = HorizontalAlignmentType.SIMPLE_CIRCULAR;
    // -------------------- Private Instance Variables --------------------
    _rootHACurve;
    _points;
    _length;
    constructor() {
        this._rootHACurve = undefined;
        this._points = [];
        this._length = 0;
    }
    //-------------------- Public Getters/Setters --------------------
    get points() {
        return this._points;
    }
    get segments() {
        return this._rootHACurve?.toArray() || [];
    }
    get length() {
        return this._length;
    }
    //----------------------- Public Methods -----------------------
    dispose() {
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            const next = currentSegment.next;
            currentSegment.dispose();
            currentSegment = next;
        }
        this._rootHACurve = undefined;
        this._points.length = 0;
        this._length = 0;
    }
    recompute(ipPoints) {
        this.dispose();
        this.initializeFromIPPoints(ipPoints);
    }
    initializeFromIPPoints(ipPoints) {
        this.dispose();
        if (ipPoints.length < 2) {
            throw new Error("At least two IP points are required");
        }
        const { curves, newPoints } = generateCompositeAlignmentFromIPs_CurveOnly(ipPoints.map((ip) => ({
            id: ip.id,
            point: new Cartesian3(ip.localPoint.x, ip.localPoint.y, 0),
        })), DEFAULT_SPEED_KMPH);
        const surveyPointsMap = new Map();
        ipPoints.forEach((ip) => surveyPointsMap.set(ip.id, ip));
        newPoints.forEach((p) => surveyPointsMap.set(p.id, {
            id: p.id,
            localPoint: p.point,
        }));
        this.initializeFromCurveData(curves, surveyPointsMap);
        this.postCompute();
        return this._rootHACurve;
    }
    initializeFromCurveData(curveData, surveyPointsMap) {
        this.dispose();
        if (curveData.length === 0) {
            throw new Error("Curve data is empty");
        }
        const getSegment = (data, _index) => {
            switch (data.type) {
                case CurveDataType.LINEAR:
                    const startPoint = surveyPointsMap.get(data.startPointId);
                    const endPoint = surveyPointsMap.get(data.endPointId);
                    if (!startPoint || !endPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HALinearSegment(startPoint, endPoint);
                case CurveDataType.CIRCULAR_SEGMENT:
                    const circularSegmentStartPoint = surveyPointsMap.get(data.startPointId);
                    const circularSegmentEndPoint = surveyPointsMap.get(data.endPointId);
                    const circularSegmentAnchorPoint = surveyPointsMap.get(data.anchorPointId);
                    if (!circularSegmentStartPoint ||
                        !circularSegmentEndPoint ||
                        !circularSegmentAnchorPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HACircularSegment(circularSegmentStartPoint, circularSegmentEndPoint, circularSegmentAnchorPoint);
                default:
                    throw new Error(`Unknown curve type: ${data.type}`);
            }
        };
        this._rootHACurve = getSegment(curveData[0], 0);
        let currentSegment = this._rootHACurve;
        for (let i = 1; i < curveData.length; i++) {
            const segment = getSegment(curveData[i], i);
            currentSegment.insertAfter(segment);
            currentSegment = segment;
        }
        this.postCompute();
    }
    getClosestPointInfo(point) {
        return HAUtils.getClosestPointInfo(point, this);
    }
    getInfoAtDistanceInBatch(sortedDistanceFromStart) {
        return HAUtils.getInfoAtDistanceInBatch(sortedDistanceFromStart, this);
    }
    getPointInfoAtDistance(distanceFromStart) {
        return HAUtils.getPointInfoAtDistance(distanceFromStart, this);
    }
    getDistanceFromStart(point) {
        return HAUtils.getDistanceFromStart(point, this);
    }
    // -------------------------- Private Methods --------------------------
    computePoints() {
        this._points.length = 0;
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            this._points.push(...currentSegment.curvePoints);
            currentSegment = currentSegment.next;
        }
    }
    computeLength() {
        this._length = 0;
        for (let i = 0; i < this.points.length - 1; i++) {
            this._length += Cartesian2.distance(this.points[i], this.points[i + 1]);
        }
    }
    postCompute() {
        this.computePoints();
        this.computeLength();
        // fix naming of points
        let currentSegment = this._rootHACurve;
        const index = 0;
        while (currentSegment) {
            if (currentSegment.type === CurveDataType.CIRCULAR_SEGMENT) {
            }
            currentSegment = currentSegment.next;
        }
    }
}
//# sourceMappingURL=simple-circular-horizontal-alignment.js.map